import cn from 'classnames';
import { Dispatch, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';

import { getSortIcon } from 'CorePackage/MainModule/common/getSortIcon';
import { sortMapper } from 'CorePackage/MainModule/common/getSortOrder';
import { getSortOrderBySelected } from 'CorePackage/MainModule/common/getSortOrderBySelected';
import { handleSortChange } from 'CorePackage/MainModule/common/handleSortChange';
import { getCoreIsLoading } from 'CorePackage/MainModule/selectors/coreSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import {
  IActionAboveUser,
  IManageTestUser,
  IManageTestUsersTableStoreProps,
} from 'APIPackage/ManageTestUserModule/interface/IManageTestUser';
import { Checkbox } from 'UIPackage/MainModule/components/Checkbox';

import {
  adminGetManageTestUsersList,
  adminGetManageTestUsersListNextPage,
  adminSetManageTestUsersListOrder,
  adminUpdateManageTestUsersList,
} from '../actions/adminAction';
import { manageTestUsersTableColumns } from '../constants/manageTestUsersTableColumns';
import {
  getAdminMangeTestUsersList,
  getAdminMangeTestUsersListPage,
  getAdminMangeTestUsersListTotalCount,
} from '../selectors/adminSelectors';
import styles from './NewsTable.module.scss';
import tableStyles from './ManageTestUsersTable.module.scss';

const ManageTestUsersTableWithoutStore = ({
  mangeTestUsersList,
  totalCount,
  loading,
  page,
}: IManageTestUsersTableStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const theadFioCellClassName = cn(tableStyles.Cell, tableStyles.FioCell, styles.TheadCell);
  const theadLoginCellClassName = cn(tableStyles.Cell, tableStyles.LoginCell, styles.TheadCell);
  const theadRoleCellClassName = cn(tableStyles.Cell, tableStyles.RoleCell, styles.TheadCell);
  const theadIsTestCellClassName = cn(tableStyles.Cell, tableStyles.IsTestCell, styles.TheadCell);
  const filterWrapperClassName = cn(styles.Row, styles.TbodyRow, tableStyles.FilterWrapper);
  const tbodyRowClassName = cn(styles.Row, styles.TbodyRow);
  const tbodyFioCellClassName = cn(tableStyles.Cell, tableStyles.FioCell, styles.TbodyCell);
  const tbodyLoginCellClassName = cn(tableStyles.Cell, tableStyles.LoginCell, styles.TbodyCell);
  const tbodyRoleCellClassName = cn(tableStyles.Cell, tableStyles.RoleCell, styles.TbodyCell);
  const tbodyIsTestCellClassName = cn(tableStyles.Cell, tableStyles.IsTestCell, styles.TbodyCell);
  const [searchInputFio, setSearchInputFio] = useState('');
  const [searchInputLogin, setSearchInputLogin] = useState('');
  const [tableColumns, setTableColumns] = useState<ITableColumn<IManageTestUser>[]>(manageTestUsersTableColumns);
  const [actionsAboveUsers, setActionsAboveUsers] = useState<IActionAboveUser[]>([]);
  const isSortAvailable = useRef(true);

  const findUsers = (fio?: string, login?: string) => {
    if (login && login.length < 3) {
      return;
    }
    if (fio && fio.length < 3) {
      return;
    }

    dispatch(adminGetManageTestUsersList(login, fio));
  };

  const debouncedFindUsers = useCallback(debounce(findUsers, 1500), []);

  useEffect(() => {
    dispatch(adminGetManageTestUsersList());
  }, [dispatch]);

  const handleSortClick = useCallback(
    (e: MouseEvent<HTMLElement>): void => {
      if (isSortAvailable.current === true) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { id } = (e.target as any).dataset;
        const newColumnsData = handleSortChange(tableColumns, id as keyof IManageTestUser);

        setTableColumns(newColumnsData);
        const newOrder = getSortOrderBySelected(newColumnsData, sortMapper);

        dispatch(adminSetManageTestUsersListOrder(newOrder));
        dispatch(adminGetManageTestUsersList(searchInputLogin, searchInputFio));
      }
    },
    [dispatch, tableColumns, searchInputFio, searchInputLogin],
  );

  const onLoadMore = useCallback(() => dispatch(adminGetManageTestUsersListNextPage()), [dispatch]);

  const table = useRef<HTMLTableElement>(null);

  useEffect(() => {
    if (page <= 0) {
      return;
    }
    setTimeout(() => {
      table.current?.scrollIntoView(false);
    }, 0);
  }, [page]);

  const getColumnClassName = useCallback((index: number) => {
    switch (index) {
      case 1:
        return theadLoginCellClassName;
      case 2:
        return theadRoleCellClassName;
      case 3:
        return theadIsTestCellClassName;
      default:
        return theadFioCellClassName;
    }
  }, []);

  const onChangeCheckbox = useCallback(
    (user: IManageTestUser) => {
      let newActions = [...actionsAboveUsers];
      const foundIndex = actionsAboveUsers.findIndex(i => i.userId === user.id);

      if (foundIndex === -1) {
        newActions.push({ userId: user.id, isTestUser: !user.isTestUser });
      } else if (user.isTestUser === !actionsAboveUsers[foundIndex].isTestUser) {
        newActions = newActions.filter((_item, i) => i !== foundIndex);
      } else {
        newActions[foundIndex].isTestUser = !newActions[foundIndex].isTestUser;
      }
      setActionsAboveUsers(newActions);
    },
    [actionsAboveUsers],
  );

  const save = useCallback(() => {
    dispatch(adminUpdateManageTestUsersList(actionsAboveUsers, searchInputLogin, searchInputFio));
  }, [actionsAboveUsers, searchInputFio, searchInputLogin]);

  return (
    <div className={tableStyles.TableWrapper}>
      <div className={styles.Header}>
        <h1 className={styles.Title}>Управление тестовыми УЗ (Методолог)</h1>
      </div>
      <div className={filterWrapperClassName}>
        <div className={tbodyFioCellClassName}>
          <TextInput
            placeholder="Введите ФИО"
            value={searchInputFio}
            onChange={e => {
              setSearchInputFio(e.target.value);
              debouncedFindUsers(e.target.value, searchInputLogin);
            }}
          />
        </div>
        <div className={tbodyLoginCellClassName}>
          <TextInput
            placeholder="Введите логин"
            value={searchInputLogin}
            onChange={e => {
              setSearchInputLogin(e.target.value);
              debouncedFindUsers(searchInputFio, e.target.value);
            }}
          />
        </div>
        <div className={tbodyRoleCellClassName} />
        <div className={tbodyIsTestCellClassName}>
          <Button
            className={tableStyles.SaveButton}
            color="primary"
            text="Сохранить"
            id="saveButton"
            type="submit"
            disabled={actionsAboveUsers.length === 0}
            onClick={save}
          />
        </div>
      </div>
      <table className={tableStyles.Table} ref={table}>
        <thead className={tableStyles.TableHead}>
          <tr className={styles.Row}>
            {tableColumns.map((row, i) => (
              <th onClick={handleSortClick} data-id={row.id} className={getColumnClassName(i)} key={i}>
                {row.name}
                {getSortIcon(tableColumns, row.id)}
              </th>
            ))}
          </tr>
        </thead>
        {!loading && (
          <tbody>
            {mangeTestUsersList.map((row, i) => {
              const checkedAction = actionsAboveUsers.find(action => action.userId === row.id);

              return (
                <tr className={tbodyRowClassName} key={i}>
                  <td className={tbodyFioCellClassName}>{row.fullName}</td>
                  <td className={tbodyLoginCellClassName}>{row.username}</td>
                  <td className={tbodyRoleCellClassName}>{row.role?.name}</td>
                  <td className={tbodyIsTestCellClassName}>
                    <div className={tableStyles.IsTestCheckboxWrapper}>
                      <Checkbox
                        id={`${row.id}`}
                        theme="system"
                        onChange={() => onChangeCheckbox(row)}
                        checked={checkedAction === undefined ? !!row.isTestUser : !!checkedAction.isTestUser}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        )}
      </table>
      {mangeTestUsersList.length < totalCount && (
        <Button type="button" text="Показать еще" color="load" onClick={onLoadMore} />
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): IManageTestUsersTableStoreProps => ({
  mangeTestUsersList: getAdminMangeTestUsersList(state),
  totalCount: getAdminMangeTestUsersListTotalCount(state),
  loading: getCoreIsLoading(state),
  page: getAdminMangeTestUsersListPage(state),
});

export const ManageTestUsersTable = connect(mapStateToProps)(ManageTestUsersTableWithoutStore);
